import { useEffect, useId, useState } from "react";
import useLastSeed from "../hooks/useLastSeed";

export default function RepsField({
  value,
  placeholder,
  setIndex = null,
  disabled = false,
  compact = false,
  // The usable LAST reps for this set, or null (§13.10).
  seedValue = null,
  onCommit,
}) {
  const [draft, setDraft] = useState(value ?? "");
  const [error, setError] = useState("");
  const errorId = useId();
  useEffect(() => setDraft(value ?? ""), [value]);
  const seed = useLastSeed({
    seedValue,
    isEmpty: draft === "" && value == null,
    disabled,
    setDraft,
  });

  function commit() {
    seed.release();
    if (draft === "") {
      setError("");
      // Passive blur of an empty field is not a commit.
      if (value != null) onCommit(null);
      return;
    }
    const parsed = Number(draft);
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 100) {
      setError("Reps must be from 1 to 100.");
      return;
    }
    setError("");
    setDraft(parsed);
    // An unchanged value is not a commit (§9.4).
    if (parsed !== value) onCommit(parsed);
  }

  function updateDraft(nextDraft) {
    if (!/^\d*$/.test(nextDraft)) return;
    setError("");
    setDraft(nextDraft);
  }

  return (
    <label className="lz-workout-number-field">
      <span className={compact ? "lz-v2-visually-hidden" : "lz-workout-field-label"}>Reps</span>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        // While an untouched LAST seed fills the field, the limit must not
        // count its digits: the first typed input replaces them (§13.10).
        maxLength={seed.seeded ? 6 : 3}
        value={draft}
        placeholder={placeholder || "--"}
        disabled={disabled}
        aria-label={setIndex == null ? "Reps" : `Set ${setIndex} reps`}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        ref={seed.inputRef}
        onClick={seed.activate}
        onChange={(event) => updateDraft(seed.resolveChange(event.target.value))}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") event.currentTarget.blur();
        }}
      />
      {error ? <span id={errorId} className="lz-workout-field-error" role="alert">{error}</span> : null}
    </label>
  );
}
